import { useState } from 'react';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Link,
    Text,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import FormBase from '@/components/forms/new/FormBase';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { validationSchema } from './consts';
import walkthroughIds from './walkthroughIds';
import { createServiceProvider } from './api';
import NameField from './formFields/NameField';
import OrganizationIdField from './formFields/OrganizationIdField';
import ApiKeyField from './formFields/ApiKeyField';
import KeyExpiryField from './formFields/KeyExpiryField';

const initialValues = {
    name: '',
    organizationId: '',
    apiKey: '',
    keyExpiry: '',
};

const breadCrumbTrail = [
    { label: 'Service Providers', path: '/globaladmin/service-providers' },
    { label: 'Create', path: '' },
];

export default function ServiceProvidersCreate({
    setServiceProvidersAction,
    setSuccessAction,
    platformId,
    requiredScope,
}) {
    const [error, setError] = useState(null);
    const { darkMode } = useSelector((state) => state.settings);
    const { createMode } = walkthroughIds;

    const finalFormSubmit = async (fields, actions) => {
        setError(null);
        setSuccessAction(null);

        createServiceProvider(fields, platformId, requiredScope)
            .then((response) => {
                if (response.status === 200) {
                    setSuccessAction('Create');
                    setServiceProvidersAction('Get');
                    setTimeout(() => setSuccessAction(null), 6000);
                } else {
                    setError(
                        response.response?.data?.message ||
                            'Failed to create service provider.',
                    );
                }
            })
            .catch((err) => {
                setError(
                    err?.response?.data?.message ||
                        err?.data?.message ||
                        'Something went wrong when creating a service provider.',
                );
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                index === 0
                                    ? () => setServiceProvidersAction('Get')
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                Create Service Provider
            </Text>
            <FormBase
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                validationSchema={validationSchema}
                ufBackBtn={createMode.ufBackBtn}
                ufSaveBtn={createMode.ufCreateBtn}
                customClick={true}
                onBack={() => setServiceProvidersAction('Get')}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <NameField
                            key="name"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <OrganizationIdField
                            key="organizationId"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ApiKeyField
                            key="apiKey"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <KeyExpiryField
                            key="keyExpiry"
                            darkMode={darkMode}
                            isEditMode={false}
                        />,
                        error && (
                            <Alert
                                key="error"
                                status="error"
                                sx={{ margin: '25px 0' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
}
